import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  getFlsaConfig,
  normalizeFlsaConfig,
  normalizeFlsaConfigDefaults,
  updateFlsaConfig,
} from '@/api/settings';
import { handleKnownSettingsApiErrors } from '@/stores/settings/errorHandling';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';
import { useFlsaSettingsForm } from '@/stores/settings/flsaSettingsForm';

export const useFlsaSettings = defineStore('flsaSettings', () => {
  const form = useFlsaSettingsForm();
  const { setLoading } = useAppLoading();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  const defaults = ref({});

  const handleValidationError = (error) => {
    const { response } = error;

    if (response && response.code === 'validation_failed') {
      const attrs = response.attributes || {};
      form.setFormValues(normalizeFlsaConfig(attrs));
      defaults.value = normalizeFlsaConfigDefaults(attrs);
      form.setBackendErrors(response.errors);
      return true;
    }

    return false;
  };

  const handleError = (error) => {
    const isKnownError = handleKnownSettingsApiErrors({ context: 'FLSA settings', error });

    if (!isKnownError) {
      addErrorSnackbar({ text: `FLSA settings - ${error.message}` });
      throw error;
    }
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });
      const data = await getFlsaConfig();
      form.setFormValues(data.attributes);
      defaults.value = data.defaults;
    } catch (error) {
      handleError(error);
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const update = async (sectionKeys = null) => {
    const isValid = sectionKeys
      ? sectionKeys.every((name) => !form.errorMessages[name])
      : form.isFormValid;

    if (!isValid) {
      return false;
    }

    try {
      setLoading({ componentLoading: true });

      const data = await updateFlsaConfig(
        form.values,
        sectionKeys ?? undefined,
      );

      if (data.success && data.attributes) {
        const sectionData = sectionKeys
          ? Object.fromEntries(sectionKeys.map((key) => [key, data.attributes[key]]))
          : data.attributes;

        form.setFormValues(sectionData);
        defaults.value = data.defaults;
        addSuccessSnackbar({
          text: 'FLSA settings have been updated',
        });
        return true;
      }
    } catch (error) {
      if (!handleValidationError(error)) {
        handleError(error);
      }
    } finally {
      setLoading({ componentLoading: false });
    }

    return false;
  };

  return { load, update, defaults };
});
